import { FC } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useLocation, useLocationAlarms } from '../../../api/apiQueries'
import {
  Box,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import { DateTime } from 'luxon'
import { GetAlarmsParams, PagedAlarmSummaries } from '../../../api/models'
import { formatIsoTimestampForLocation } from '../../../utils/dateUtils'
import { getAlarmSummaryCategoryDisplayName } from '../../../constants/AlarmSummaryCategoryDisplayNames'
import { LocationSearch } from '../../../components/LocationSearch'
import { useLocationId } from '../../../hooks/useLocationId'
import { Banner, BannerType } from '../../../components/Banner/Banner'

export const AlarmSearch: FC = () => {
  const { locationId, setLocationId } = useLocationId()

  const startDate = DateTime.now().minus({ days: 30 }).startOf('day')

  const endDate = DateTime.now().endOf('day').toISO()

  const interval = `${startDate}/${endDate}`
  const params: GetAlarmsParams = {
    interval: interval,
    limit: 50,
    nextToken: undefined
  }

  const alarmsQuery = useLocationAlarms(locationId, params)
  const locationQuery = useLocation(locationId)
  const alarmsData: PagedAlarmSummaries | undefined = alarmsQuery.data
  const location = locationQuery.data
  const alarms = alarmsData?.items ?? []
  const isLoading = alarmsQuery.isLoading || locationQuery.isLoading
  const navigate = useNavigate()

  return !locationId ? (
    <Stack spacing={'var(--spacing-xl)'} sx={{ marginTop: 'var(--spacing-xl)' }}>
      <Banner type={BannerType.Warning} title={'Select a Location'} body={'You must have a location selected to view its alarms.'} />
      <LocationSearch onSearchChange={sid => setLocationId(sid)} shouldAllowCameraIdSearch={false} />
    </Stack>
  ) : (
    <Paper data-testid="alarms" sx={{ backgroundColor: 'var(--bg-surface)' }}>
      <Typography variant="h2" p="var(--spacing-s)" data-testid="alarms-table-heading">
        Alarms
      </Typography>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ '&>th': { color: 'var(--text-secondary)', fontWeight: 500, fontSize: 'var(--size-2xs)' } }}>
              <TableCell>ALARM ID</TableCell>
              <TableCell>TYPE</TableCell>
              <TableCell>DATE &amp; TIME</TableCell>
              <TableCell>CATEGORY</TableCell>
              <TableCell>SPECIALIST</TableCell>
              <TableCell>SID</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {location &&
              alarms.map(alarm => {
                // this fixes a stupid bug that is up a few services
                const monitoringSpecialistId =
                  alarm.monitoring.monitoringSpecialist?.id === 'undefined' ? undefined : alarm.monitoring.monitoringSpecialist?.id
                const monitoringSpecialistName = alarm.monitoring.monitoringSpecialist?.name
                return (
                  alarm && (
                    <TableRow
                      key={alarm.id}
                      sx={{
                        '&:nth-of-type(odd)': { backgroundColor: 'var(--bg-surface-low)' },
                        '&:hover': { backgroundColor: 'var(--shape-neutral)', cursor: 'pointer' }
                      }}
                      onClick={() => navigate(`../../alarm/${alarm.id}`)}
                      data-testid="alart-table-entry"
                    >
                      <TableCell>
                        <Link
                          component={NavLink}
                          sx={{ color: 'var(--text-info)' }}
                          to={`../../alarm/${alarm.id}`}
                          // stop propagation to prevent the row onClick from firing. this allows the link to be opened in a new tab.
                          onClick={e => e.stopPropagation()}
                        >
                          {alarm.id}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Chip label={'Alarm'} sx={{ '&': { backgroundColor: 'var(--button-disabled-bg)', borderRadius: 'var(--radius-m)' } }} />
                      </TableCell>
                      <TableCell>{formatIsoTimestampForLocation(alarm.createdTimestamp, location)}</TableCell>
                      <TableCell>{getAlarmSummaryCategoryDisplayName(alarm.category)}</TableCell>
                      <TableCell>
                        {monitoringSpecialistId && monitoringSpecialistName && `${monitoringSpecialistName} - ${monitoringSpecialistId}`}
                        {monitoringSpecialistId}
                        {monitoringSpecialistName}
                      </TableCell>
                      <TableCell>{alarm.locationId}</TableCell>
                    </TableRow>
                  )
                )
              })}
            {!isLoading && alarms.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align={'center'}>
                  No results found
                </TableCell>
              </TableRow>
            )}
            {isLoading && (
              <>
                {alarms.length === 0 && (
                  <TableRow>
                    <TableCell>&nbsp;</TableCell>
                  </TableRow>
                )}
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <CircularProgress />
                </Box>
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
}
